/**
 * CompanyListPage - 세금계산서 발급을 위한 회사 목록 페이지
 * Issue #89: API 연동
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Plus, Loader2 } from 'lucide-react';
import svgPaths from '../imports/svg-xqx4wkgsq1';
import {
  type Company,
  type CompanyFormData,
  INITIAL_FORM_DATA,
  companyTranslations,
  CompanyForm,
  CompanyCard,
  CompanyEmptyState,
  DeleteConfirmDialog,
} from './company';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/AppContext';
import { logger } from '@/utils/logger';
import { useAuth } from '@/contexts/AuthContext';
import { companiesApi } from '@/lib/api/companies';
import { ApiClientError } from '@/lib/api/client';
import type { CustomerCompany } from '@/lib/api/types';

interface CompanyListPageProps {
  onBack: () => void;
  onCompanySelect: (company: Company) => void;
}

/**
 * API 응답(CustomerCompany)을 프론트엔드 타입(Company)으로 변환
 */
function mapCustomerCompanyToCompany(apiCompany: CustomerCompany): Company {
  return {
    id: String(apiCompany.id),
    name: apiCompany.name,
    rfc: apiCompany.rfc,
    email: apiCompany.email,
    phone: apiCompany.phone,
    address: apiCompany.address,
    city: apiCompany.city,
    state: apiCompany.state,
    postalCode: apiCompany.postal_code,
    taxRegime: apiCompany.tax_regime,
    cfdiUse: apiCompany.cfdi_use,
    isDefault: apiCompany.is_default,
    createdAt: apiCompany.created_at,
  };
}

export function CompanyListPage({ onBack, onCompanySelect }: CompanyListPageProps) {
  const { language } = useLanguage();
  const { isLoggedIn } = useAuth();
  const isMountedRef = useRef(true);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CompanyFormData>(INITIAL_FORM_DATA);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);

  const t = companyTranslations[language];

  // 회사 목록 조회
  const fetchCompanies = useCallback(async () => {
    if (!isLoggedIn) {
      setCompanies([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await companiesApi.getCompanies();
      if (!isMountedRef.current) return;

      if (response.success && response.data) {
        setCompanies(response.data.map(mapCustomerCompanyToCompany));
      }
    } catch (err) {
      if (!isMountedRef.current) return;

      if (err instanceof ApiClientError && err.status === 401) {
        setCompanies([]);
        return;
      }
      logger.error('[CompanyListPage] 회사 목록 조회 실패:', err);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [isLoggedIn]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchCompanies();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchCompanies]);

  // 회사 추가/수정 핸들러
  const handleSubmit = async () => {
    if (!formData.name || !formData.rfc || !formData.address ||
        !formData.taxRegime || !formData.cfdiUse || !formData.postalCode) {
      toast.warning(t.fillRequired);
      return;
    }

    setIsSaving(true);
    try {
      if (editingCompanyId) {
        // 기존 회사 수정
        const response = await companiesApi.updateCompany(Number(editingCompanyId), {
          name: formData.name,
          rfc: formData.rfc,
          email: formData.email || undefined,
          phone: formData.phone || undefined,
          address: formData.address,
          city: formData.city || undefined,
          state: formData.state || undefined,
          postal_code: formData.postalCode || undefined,
          tax_regime: formData.taxRegime || undefined,
          cfdi_use: formData.cfdiUse || undefined,
        });

        if (response.success && response.data) {
          setCompanies(prev => prev.map(company =>
            company.id === editingCompanyId
              ? mapCustomerCompanyToCompany(response.data)
              : company
          ));
          toast.success(t.companyUpdated);
        }
        setEditingCompanyId(null);
      } else {
        // 새 회사 추가
        const response = await companiesApi.createCompany({
          name: formData.name,
          rfc: formData.rfc,
          email: formData.email || undefined,
          phone: formData.phone || undefined,
          address: formData.address,
          city: formData.city || undefined,
          state: formData.state || undefined,
          postal_code: formData.postalCode || undefined,
          tax_regime: formData.taxRegime || undefined,
          cfdi_use: formData.cfdiUse || undefined,
        });

        if (response.success && response.data) {
          setCompanies(prev => [...prev, mapCustomerCompanyToCompany(response.data)]);
          toast.success(t.companyRegistered);
        }
      }

      setFormData(INITIAL_FORM_DATA);
      setShowAddForm(false);
    } catch (err) {
      logger.error('[CompanyListPage] 회사 저장 실패:', err);
      toast.error(err instanceof Error ? err.message : '저장에 실패했습니다');
    } finally {
      setIsSaving(false);
    }
  };

  // 취소 핸들러
  const handleCancel = () => {
    setFormData(INITIAL_FORM_DATA);
    setShowAddForm(false);
    setEditingCompanyId(null);
  };

  // 회사 수정 핸들러
  const handleEditCompany = (company: Company) => {
    setFormData({
      name: company.name,
      rfc: company.rfc,
      address: company.address || '',
      phone: company.phone || '',
      email: company.email || '',
      city: company.city || '',
      state: company.state || '',
      postalCode: company.postalCode || '',
      taxRegime: company.taxRegime || '',
      cfdiUse: company.cfdiUse || ''
    });
    setEditingCompanyId(company.id);
    setShowAddForm(true);
  };

  // 삭제 클릭 핸들러
  const handleDeleteClick = (company: Company) => {
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
  };

  // 삭제 확인 핸들러
  const handleDeleteConfirm = async () => {
    if (!companyToDelete) return;

    try {
      await companiesApi.deleteCompany(Number(companyToDelete.id));
      setCompanies(prev => prev.filter(company => company.id !== companyToDelete.id));
      toast.success(t.companyDeleted);
    } catch (err) {
      logger.error('[CompanyListPage] 회사 삭제 실패:', err);
      toast.error(err instanceof Error ? err.message : '삭제에 실패했습니다');
    } finally {
      setCompanyToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted text-foreground">
      {/* Header - Fixed */}
      <div className="fixed top-0 left-0 right-0 bg-primary h-[50px] z-50">
        <div className="flex items-center justify-center relative h-[50px] px-[20px]">
          <button
            onClick={onBack}
            className="absolute left-[20px] flex items-center justify-center w-[32px] h-[32px]"
          >
            <svg className="w-[12px] h-[21px]" fill="none" preserveAspectRatio="none" viewBox="0 0 12 21">
              <path d={svgPaths.p27596280} fill="white" />
            </svg>
          </button>
          <h1
            className="font-['Noto_Sans:Bold',_'Noto_Sans_KR:Bold',_sans-serif] font-bold text-[16px] text-white tracking-[-0.32px] text-center uppercase"
            style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
          >
            {t.title}
          </h1>
        </div>
      </div>

      <div className="pt-[50px] p-3 space-y-3">
        {/* 로딩 상태 */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}

        {/* 회사 추가/수정 폼 */}
        {showAddForm && (
          <CompanyForm
            formData={formData}
            onFormChange={setFormData}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isEditing={!!editingCompanyId}
            translations={t}
            isSaving={isSaving}
          />
        )}

        {/* 등록된 회사 목록 */}
        {!isLoading && (
          <div>
            <div className="mb-2">
              <h2 className="text-[15px] font-semibold text-foreground">
                {t.registeredCompanies}
              </h2>
            </div>

            {companies.length === 0 ? (
              <CompanyEmptyState translations={t} />
            ) : (
              <div className="space-y-2">
                {companies.map((company) => (
                  <CompanyCard
                    key={company.id}
                    company={company}
                    onEdit={handleEditCompany}
                    onDelete={handleDeleteClick}
                    onSelect={onCompanySelect}
                    translations={t}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* 새 회사 등록 버튼 */}
        {!showAddForm && !isLoading && (
          <Button
            onClick={() => setShowAddForm(true)}
            className="w-full bg-secondary hover:bg-secondary/90 text-white h-10 rounded-[5px] flex items-center justify-center"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            <span className="text-[14px] font-medium">{t.addNewCompany}</span>
          </Button>
        )}
      </div>

      {/* 삭제 확인 다이얼로그 */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        company={companyToDelete}
        onConfirm={handleDeleteConfirm}
        translations={t}
      />
    </div>
  );
}
